// src/scenes/contracts/ContractFormPage/ValoresTab.jsx
import {
  Box,
  Collapse,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from '../../../hooks/useTranslation';
import { formatNumericInput } from '../../../utils';

const fieldLabelSx = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#9e9e9e',
  mb: 0.5,
  letterSpacing: '0.5px',
};

const sectionTitleSx = {
  fontWeight: 700,
  fontSize: '13px',
  mb: 2,
  letterSpacing: '0.5px',
  color: '#333',
};

const inputSx = {
  '& .MuiInputBase-root': {
    fontSize: '14px',
    backgroundColor: '#fff',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
};

const selectSx = {
  backgroundColor: '#fff',
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bdbdbd' },
};

const LabeledField = ({ label, children }) => (
  <Box>
    <Typography sx={fieldLabelSx}>{label}</Typography>
    {children}
  </Box>
);

const ValoresTab = ({ formData, onChange, options, errors }) => {
  const { t } = useTranslation();
  const s = 'contractForm.valores';
  const [showInstallments, setShowInstallments] = useState(true);

  // Mask handler for currency/percent fields
  const handleFormatted = (field, type) => (e) => {
    onChange({ [field]: formatNumericInput(e.target.value, { type }) });
  };

  const handle = (field) => (e) => onChange({ [field]: e.target.value });

  return (
    <Box>
      {/* RESUMO FINANCEIRO */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Typography sx={sectionTitleSx}>{t(`${s}.financialSummary`)}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.totalValue`)}>
              <TextField
                value={formData.valorTotal}
                onChange={handleFormatted('valorTotal', 'currency')}
                size="small"
                fullWidth
                placeholder="0,00"
                error={!!errors?.valorTotal}
                helperText={errors?.valorTotal ? t('contractForm.validation.required') : ''}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.financedValue`)}>
              <TextField
                value={formData.valorFinanciado}
                onChange={handleFormatted('valorFinanciado', 'currency')}
                size="small"
                fullWidth
                placeholder="0,00"
                error={!!errors?.valorFinanciado}
                helperText={errors?.valorFinanciado ? t('contractForm.validation.required') : ''}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.downPayment`)}>
              <TextField
                value={formData.valorEntrada}
                onChange={handleFormatted('valorEntrada', 'currency')}
                size="small"
                fullWidth
                placeholder="0,00"
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.installments`)}>
              <TextField
                value={formData.parcelas}
                onChange={handle('parcelas')}
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 1 }}
                error={!!errors?.parcelas}
                helperText={errors?.parcelas ? t('contractForm.validation.required') : ''}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.installmentValue`)}>
              <TextField
                value={formData.valorParcela}
                onChange={handleFormatted('valorParcela', 'currency')}
                size="small"
                fullWidth
                placeholder="0,00"
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Box>

      {/* TAXAS */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Typography sx={sectionTitleSx}>{t(`${s}.taxes`)}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.monthlyInterest`)}>
              <TextField
                value={formData.jurosMensal}
                onChange={handleFormatted('jurosMensal', 'percent')}
                size="small"
                fullWidth
                placeholder="0,00"
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.totalInterest`)}>
              <TextField
                value={formData.totalJuros}
                onChange={handleFormatted('totalJuros', 'currency')}
                size="small"
                fullWidth
                placeholder="0,00"
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.iof`)}>
              <TextField
                value={formData.iof}
                onChange={handleFormatted('iof', 'percent')}
                size="small"
                fullWidth
                placeholder="0,00"
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.tac`)}>
              <TextField
                value={formData.tac}
                onChange={handleFormatted('tac', 'percent')}
                size="small"
                fullWidth
                placeholder="0,00"
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.cet`)}>
              <TextField
                value={formData.cet}
                onChange={handleFormatted('cet', 'percent')}
                size="small"
                fullWidth
                placeholder="0,00"
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.operational`)}>
              <TextField
                value={formData.operacional}
                onChange={handleFormatted('operacional', 'currency')}
                size="small"
                fullWidth
                placeholder="0,00"
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>

          {/* Dropdowns */}
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.index`)}>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.indice}
                  onChange={handle('indice')}
                  sx={selectSx}
                >
                  {(options?.indices ?? []).map((idx) => (
                    <MenuItem key={idx.value} value={idx.value} sx={{ fontSize: '14px' }}>
                      {idx.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.calculationBase`)}>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.baseCalculo}
                  onChange={handle('baseCalculo')}
                  sx={selectSx}
                >
                  {(options?.basesCalculo ?? []).map((b) => (
                    <MenuItem key={b.value} value={b.value} sx={{ fontSize: '14px' }}>
                      {b.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.calculationTable`)}>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.tabelaCalculo}
                  onChange={handle('tabelaCalculo')}
                  sx={selectSx}
                >
                  {(options?.tabelasCalculo ?? []).map((tb) => (
                    <MenuItem key={tb.value} value={tb.value} sx={{ fontSize: '14px' }}>
                      {tb.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LabeledField>
          </Grid>
        </Grid>
      </Box>

      {/* PARCELAS DO CONTRATO */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            mb: showInstallments ? 2 : 0,
          }}
          onClick={() => setShowInstallments((v) => !v)}
        >
          <Typography sx={sectionTitleSx}>{t(`${s}.installmentsTable`)}</Typography>
          {showInstallments
            ? <ExpandLessIcon sx={{ color: '#9e9e9e' }} />
            : <ExpandMoreIcon sx={{ color: '#9e9e9e' }} />}
        </Box>
        <Collapse in={showInstallments}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    t(`${s}.columns.installment`),
                    t(`${s}.columns.day`),
                    t(`${s}.columns.amortization`),
                    t(`${s}.columns.interest`),
                    t(`${s}.columns.indexer`),
                    t(`${s}.columns.value`),
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{ fontWeight: 600, fontSize: '11px', color: '#555', letterSpacing: '0.3px' }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{ textAlign: 'center', fontSize: '13px', color: '#9e9e9e', py: 3 }}
                  >
                    {t(`${s}.noInstallments`)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default ValoresTab;
